import { Link, useParams, useRouteMatch } from 'react-router-dom';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Box } from '@givebutter/primitives/box';
import { BoxIcon } from '@givebutter/primitives/box-icon';
import { Button } from '@givebutter/primitives/button';
import { Separator } from '@givebutter/primitives/separator';
import { maskDecimal } from '@givebutter/util';
import { faChevronDown, faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { faRectangleHistory, faTag, faTicket } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Collapsible from '@radix-ui/react-collapsible';
import { fetchTicketById } from 'api/tickets';
import { SupporterDetails } from 'assets/js/pages/transactions/SupporterDetails';
import * as TicketLineItem from 'assets/js/pages/transactions/TicketLineItem';
import { TransactionCampaign } from 'assets/js/pages/transactions/TransactionCampaign';
import * as Drawer from 'components/drawer';
import { DrawerIncrementor } from 'components/drawer/DrawerIncrementor';
import * as DropdownMenu from 'components/dropdown-menu';
import * as TicketActions from 'components/pages/tickets/TicketActions';
import { TicketCustomFields } from 'components/pages/tickets/TicketCustomFields';
import { TicketEventDetails } from 'components/pages/tickets/TicketEventDetails';
import { TicketItemDownloadButton } from 'components/pages/tickets/TicketItemDownloadButton';
import { TicketScannedBy } from 'components/pages/tickets/TicketScannedBy';
import { TicketSeatingInfo } from 'components/pages/tickets/TicketSeatingInfo';
import { TicketStatusBadge } from 'components/pages/tickets/TicketStatusBadge';
import { ToggleTicketCheckInButton } from 'components/pages/tickets/ToggleTicketCheckInButton';
import { transformTicketNumber } from 'components/pages/tickets/transform';
import { useCurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentTransactionOrById } from 'customHooks/useCurrentTransactionOrById';
import { dateFromServer } from 'helpers/dates';
import { DateTime } from 'luxon';
import { accountQueryKeys } from 'queries/account-query-keys';
import { Ticket } from 'types/domain';

const TicketDrawer = () => {
    const { accountId } = useCurrentAccount();
    const match = useRouteMatch();
    const currencySymbol = useCurrencySymbol();
    const { ticket_id, transaction_id } = useParams<{
        ticket_id: string;
        transaction_id?: string;
    }>();
    const ticketId = parseInt(ticket_id);
    const hasTransactionParam = !!transaction_id;
    const isIndex = match.path.includes('/accounts/:account_id/tickets');

    const { data: ticket } = useSuspenseQuery({
        queryKey: accountQueryKeys.ticketById(accountId, ticketId),
        queryFn: () => fetchTicketById(ticketId),
        select: data => ({
            ...data,
            uid_suffix: transformTicketNumber(data.uid_suffix),
        }),
    });

    const { data: transaction } = useCurrentTransactionOrById({
        transactionId: ticket?.transaction_id!,
        select: data => ({
            ...data,
            relatedTickets: (data?.tickets ?? []).filter(ticket => ticket.id !== ticketId),
        }),
    });

    const campaignId = transaction?.campaign?.id;
    const bundle = ticket?.bundle;

    return (
        <>
            <Drawer.Header className="relative border-none bg-cover !p-0">
                <div className="absolute inset-0 bg-purple-500" />
                <div className="absolute inset-0 bg-gradient-to-l from-blue-900/75 via-transparent to-transparent" />{' '}
                <div className={'flex w-full flex-col gap-6 p-6'}>
                    <div className={'z-10 flex items-start justify-between gap-2'}>
                        <BoxIcon size="lg">
                            <FontAwesomeIcon size={'lg'} icon={faTicket} />
                        </BoxIcon>
                        <div className={'flex items-center gap-2'}>
                            {isIndex && <DrawerIncrementor resource="tickets" />}
                            <ToggleTicketCheckInButton ticket={ticket} />
                            <TicketItemDownloadButton item={ticket} />
                            <TicketActions.Root ticket={ticket}>
                                <DropdownMenu.Root>
                                    <DropdownMenu.EllipsisTrigger className="h-8 rounded-md border-gray-300 bg-white px-3.5 text-gray-600" />
                                    <DropdownMenu.Content sideOffset={5} align={'end'}>
                                        {!hasTransactionParam && <TicketActions.ViewTransaction />}
                                        <TicketActions.Reassign />
                                        <TicketActions.Revoke />
                                        <TicketActions.Resend />
                                    </DropdownMenu.Content>
                                </DropdownMenu.Root>
                            </TicketActions.Root>
                        </div>
                    </div>
                    <div className="z-layerOne flex flex-col gap-1">
                        <p className={'text-2xl font-medium text-white'}>{ticket?.title}</p>

                        <div className="flex items-center gap-2">
                            <div className="flex flex-wrap items-center gap-2">
                                <div className="flex items-center gap-2">
                                    <div className={'text-lg text-white'}>{ticket?.uid_suffix}</div>
                                    <Separator
                                        orientation={'vertical'}
                                        className={'!h-4 bg-gray-300'}
                                    />
                                </div>
                                {bundle && (
                                    <div className="flex items-center gap-1">
                                        <div className="flex items-center gap-2">
                                            <FontAwesomeIcon
                                                size={'xs'}
                                                icon={faRectangleHistory}
                                                className="text-white"
                                            />
                                            <p
                                                className={
                                                    'max-w-[300px] truncate font-medium text-white'
                                                }
                                            >
                                                {bundle?.title}
                                            </p>
                                        </div>
                                        <span className={'text-white'}>
                                            {`B-${bundle?.id.toString()}`}
                                        </span>
                                    </div>
                                )}
                                <TicketStatusBadge ticket={ticket} />
                            </div>
                        </div>
                    </div>
                </div>
            </Drawer.Header>
            <Drawer.Body className="p-0">
                <div className={'flex flex-col gap-6'}>
                    <div className={'overflow-clip'}>
                        <div className={'relative flex flex-col gap-8 overflow-visible p-6'}>
                            <Box className="absolute -top-[74px] left-0 right-0 m-auto aspect-square w-[90px] rounded-full bg-purple-500" />
                            {ticket.contact ? <SupporterDetails contact={ticket.contact} /> : null}
                            <TicketSeatingInfo ticket={ticket} />
                            {ticket.latest_checkin?.user && (
                                <TicketScannedBy checkin={ticket.latest_checkin} />
                            )}
                            {transaction && <TransactionCampaign transaction={transaction} />}
                            <TicketEventDetails ticket={ticket} />
                            <Collapsible.Root className={'group'}>
                                <div className={'flex flex-col gap-2'}>
                                    <p className={'text-sm font-medium'}>Purchase details</p>
                                    <Collapsible.Trigger>
                                        <div className={'flex flex-col items-start'}>
                                            <div className="flex items-start gap-2">
                                                <FontAwesomeIcon
                                                    className={
                                                        'group-radix-state-open:rotate-90 pt-1 text-gray-700'
                                                    }
                                                    icon={faChevronRight}
                                                />
                                                <div className="flex flex-col items-start">
                                                    <div className="flex items-center gap-2">
                                                        <p
                                                            className={
                                                                'text-base font-medium text-gray-700'
                                                            }
                                                        >
                                                            {transaction?.total_display === 0
                                                                ? 'Free'
                                                                : `${currencySymbol}${maskDecimal(
                                                                      transaction?.total_display,
                                                                  )}`}
                                                        </p>

                                                        <div className="mt-[1px]">
                                                            <Link
                                                                to={`${match.url}/transactions/${transaction.id}`}
                                                                className="text-sm font-medium"
                                                            >
                                                                View transaction
                                                            </Link>
                                                        </div>
                                                    </div>

                                                    {bundle && (
                                                        <div className={'flex'}>
                                                            <p className="text-xs text-gray-700">
                                                                Bought as a bundle
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </Collapsible.Trigger>
                                    <Collapsible.Content className={'bg-gray-25 rounded-lg p-3'}>
                                        <div className={'flex flex-col gap-2 pl-1'}>
                                            <div className="flex">
                                                <p className="w-32 text-sm font-medium">
                                                    Purchase Date
                                                </p>
                                                <p className="min-w-0 flex-1 text-sm text-gray-600">
                                                    {dateFromServer(
                                                        transaction?.transacted_at || '',
                                                    ).toLocaleString(DateTime.DATE_FULL)}
                                                </p>
                                            </div>
                                            <div className="flex">
                                                <p className="w-32 text-sm font-medium">
                                                    Transaction ID
                                                </p>
                                                <p className="min-w-0 flex-1 text-sm text-gray-600">
                                                    #{transaction?.number}
                                                </p>
                                            </div>
                                            {transaction?.discount && (
                                                <div className="flex">
                                                    <p className="w-32 text-sm font-medium">
                                                        Promo code
                                                    </p>
                                                    <div className="flex min-w-0 flex-1 items-center gap-1">
                                                        <FontAwesomeIcon
                                                            className={'text-gray-600'}
                                                            size={'sm'}
                                                            icon={faTag}
                                                        />
                                                        <p className="text-sm text-gray-600">
                                                            {transaction?.discount.code}
                                                        </p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </Collapsible.Content>
                                </div>
                            </Collapsible.Root>
                            <TicketCustomFields ticket={ticket} campaignId={campaignId} />
                            {bundle?.tickets?.length && (
                                <Collapsible.Root defaultOpen={true}>
                                    <Collapsible.Trigger
                                        className={
                                            'group flex w-full items-center justify-between gap-2 rounded-md bg-gray-100 px-3 py-2'
                                        }
                                    >
                                        <div className="flex gap-2 text-left">
                                            <div className="flex items-center gap-2">
                                                <FontAwesomeIcon
                                                    size="sm"
                                                    icon={faRectangleHistory}
                                                    className="text-purple-500"
                                                />
                                                <div>
                                                    <p className={'text-sm font-medium'}>
                                                        {bundle.title}
                                                    </p>
                                                </div>
                                            </div>
                                            <p className={'text-sm text-gray-500'}>
                                                {`B-${bundle.id.toString()}`}
                                            </p>
                                        </div>
                                        <FontAwesomeIcon
                                            className={'group-radix-state-open:rotate-180'}
                                            icon={faChevronDown}
                                        />
                                    </Collapsible.Trigger>
                                    <Collapsible.Content
                                        className={'relative flex flex-col gap-4 px-3 py-4'}
                                    >
                                        {bundle.tickets.map((ticket: Ticket) => (
                                            <TicketLineItem.Root key={ticket.id} ticket={ticket}>
                                                <TicketLineItem.Icon />
                                                <TicketLineItem.DetailsContainer>
                                                    <TicketLineItem.Details />
                                                </TicketLineItem.DetailsContainer>
                                            </TicketLineItem.Root>
                                        ))}
                                    </Collapsible.Content>
                                </Collapsible.Root>
                            )}

                            {transaction?.relatedTickets &&
                                transaction.relatedTickets.length > 0 && (
                                    <Collapsible.Root defaultOpen={true}>
                                        <Collapsible.Trigger
                                            className={
                                                'group flex w-full items-center justify-between gap-2 rounded-md bg-gray-100 px-3 py-2'
                                            }
                                        >
                                            <p className={'text-sm font-medium'}>Related tickets</p>
                                            <FontAwesomeIcon
                                                className={'group-radix-state-open:rotate-180'}
                                                icon={faChevronDown}
                                            />
                                        </Collapsible.Trigger>
                                        <Collapsible.Content
                                            className={'relative flex flex-col gap-4 px-3 py-4'}
                                        >
                                            {transaction.relatedTickets.map((ticket: Ticket) => (
                                                <TicketLineItem.Root
                                                    key={ticket.id}
                                                    ticket={ticket}
                                                >
                                                    <TicketLineItem.Icon />
                                                    <TicketLineItem.DetailsContainer>
                                                        <TicketLineItem.Details />
                                                    </TicketLineItem.DetailsContainer>
                                                </TicketLineItem.Root>
                                            ))}
                                        </Collapsible.Content>
                                    </Collapsible.Root>
                                )}
                        </div>
                    </div>
                </div>
            </Drawer.Body>
            <Drawer.Footer className={'sm:hidden'}>
                <Drawer.Cancel asChild={true}>
                    <Button variant={'outline'} color={'neutral'}>
                        Close
                    </Button>
                </Drawer.Cancel>
            </Drawer.Footer>
        </>
    );
};

export { TicketDrawer };
